import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { firestore, auth } from "./firebase";
import { DateRange } from "react-date-range";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./AdminPanel.css";
import { enUS } from "date-fns/locale";

function calculateTotalHours(checkIn, checkOut, lunchStart, lunchEnd) {
  if (!checkIn || !checkOut) return 0;
  
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  
  // Set workday boundaries (9 AM to 6 PM)
  const workStart = new Date(start);
  workStart.setHours(9, 0, 0, 0);
  
  const workEnd = new Date(start);
  workEnd.setHours(18, 0, 0, 0);
  
  // Adjust check-in time if before 9 AM
  const effectiveStart = start < workStart ? workStart : start;
  
  // Adjust check-out time if after 6 PM
  const effectiveEnd = end > workEnd ? workEnd : end;
  
  // Calculate total time before considering lunch break
  let totalMs = effectiveEnd - effectiveStart;
  if (totalMs <= 0) return 0;
  
  // Subtract lunch break if exists
  if (lunchStart && lunchEnd) {
    const lunchStartTime = new Date(lunchStart);
    const lunchEndTime = new Date(lunchEnd);
    
    // Only subtract if lunch break is within working hours
    if (lunchStartTime < effectiveEnd && lunchEndTime > effectiveStart) {
      const lunchMs = lunchEndTime - lunchStartTime;
      totalMs -= lunchMs;
    }
  }
  
  // Convert to hours
  return totalMs / (1000 * 60 * 60);
}
function formatHours(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
}

function isLateCheckIn(checkIn) {
  if (!checkIn) return false;
  const time = new Date(checkIn);
  return time.getHours() >= 9;
}

function isEarlyCheckOut(checkOut) {
  if (!checkOut) return false;
  const time = new Date(checkOut);
  return time.getHours() < 18;
}

export default function AdminPanel() {
  const [filtered, setFiltered] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState("");
  const [range, setRange] = useState([{ startDate: null, endDate: null, key: "selection" }]);
  const [showRange, setShowRange] = useState(false);
  const [activeTab, setActiveTab] = useState("attendance");

  useEffect(() => {
    fetchAttendance();
  }, []);

 
// Update the rows creation in fetchAttendance to include lunch times
const fetchAttendance = async () => {
  setLoading(true);
  const snap = await getDocs(collection(firestore, "attendanceRecords"));
  let rows = [];
  snap.forEach(docSnap => {
    const data = docSnap.data();
    rows.push({
      userId: data.userId,
      date: data.date,
      name: data.name || "",
      checkIn: data.checkIn || "",
      lunchStart: data.lunchStart || "",
      lunchEnd: data.lunchEnd || "",
      checkOut: data.checkOut || "",
      totalHours: calculateTotalHours(
        data.checkIn, 
        data.checkOut,
        data.lunchStart,
        data.lunchEnd
      ),
      isLate: isLateCheckIn(data.checkIn),
      isEarly: isEarlyCheckOut(data.checkOut)
    });
  });
  rows.sort((a, b) => b.date.localeCompare(a.date));
  setAllRows(rows);
  setFiltered(rows);
  setLoading(false);
};
 const handleSearch = () => {
  let rows = [...allRows];
  const { startDate, endDate } = range[0];
  
  if (startDate && endDate) {
    // Convert the selected dates to Date objects at midnight for accurate comparison
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    
    rows = rows.filter(row => {
      const rowDate = new Date(row.date);
      return rowDate >= start && rowDate <= end;
    });
  }
  
  if (searchName.trim()) {
    const nameLower = searchName.trim().toLowerCase();
    rows = rows.filter(row => row.name.toLowerCase().includes(nameLower));
  }
  
  setFiltered(rows);
};

  const handleShowAll = () => {
    setRange([{ startDate: null, endDate: null, key: "selection" }]);
    setSearchName("");
    setFiltered(allRows);
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, "attendance_report.xlsx");
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Attendance Report", 10, 10);
    autoTable(doc, {
      startY: 18,
      head: [["Date", "Name", "Check In", "Lunch Start", "Lunch End", "Check Out"]],
      body: filtered.map(row => [
        row.date,
        row.name,
        row.checkIn ? new Date(row.checkIn).toLocaleTimeString() : "",
        row.lunchStart ? new Date(row.lunchStart).toLocaleTimeString() : "",
        row.lunchEnd ? new Date(row.lunchEnd).toLocaleTimeString() : "",
        row.checkOut ? new Date(row.checkOut).toLocaleTimeString() : ""
      ]),
    });
    doc.save("attendance_report.pdf");
  };

const getHoursWorkedRows = () => {
  let rows = [...allRows];
  const { startDate, endDate } = range[0];
  
  // Filter by date range
  if (startDate && endDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    
    rows = rows.filter(row => {
      const rowDate = new Date(row.date);
      return rowDate >= start && rowDate <= end;
    });
  }
  
  // Filter by name if search term exists
  if (searchName.trim()) {
    const nameLower = searchName.trim().toLowerCase();
    rows = rows.filter(row => row.name.toLowerCase().includes(nameLower));
  }
  
  // Calculate working hours (9 AM to 6 PM, excluding lunch)
  const userMap = {};
  rows.forEach(row => {
    if (!userMap[row.name]) userMap[row.name] = 0;
    
    // Calculate daily working hours
    const dailyHours = calculateWorkingHours(
      row.checkIn,
      row.checkOut,
      row.lunchStart,
      row.lunchEnd
    );
    
    userMap[row.name] += dailyHours;
  });
  
  return Object.entries(userMap).map(([name, hours]) => ({
    name,
    totalHours: hours
  }));
};

// Helper function to calculate working hours (9 AM to 6 PM, excluding lunch)
const calculateWorkingHours = (checkIn, checkOut, lunchStart, lunchEnd) => {
  if (!checkIn || !checkOut) return 0;
  
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  
  // Set workday boundaries (9 AM to 6 PM)
  const workStart = new Date(start);
  workStart.setHours(9, 0, 0, 0);
  
  const workEnd = new Date(start);
  workEnd.setHours(18, 0, 0, 0);
  
  // Adjust check-in time if before 9 AM
  const effectiveStart = start < workStart ? workStart : start;
  
  // Adjust check-out time if after 6 PM
  const effectiveEnd = end > workEnd ? workEnd : end;
  
  // Calculate total time before considering lunch break
  let totalMs = effectiveEnd - effectiveStart;
  if (totalMs <= 0) return 0;
  
  // Subtract lunch break if exists and within working hours
  if (lunchStart && lunchEnd) {
    const lunchStartTime = new Date(lunchStart);
    const lunchEndTime = new Date(lunchEnd);
    
    // Only subtract if lunch break overlaps with working hours
    if (lunchStartTime < effectiveEnd && lunchEndTime > effectiveStart) {
      const adjustedLunchStart = lunchStartTime < effectiveStart ? effectiveStart : lunchStartTime;
      const adjustedLunchEnd = lunchEndTime > effectiveEnd ? effectiveEnd : lunchEndTime;
      const lunchMs = adjustedLunchEnd - adjustedLunchStart;
      totalMs -= lunchMs > 0 ? lunchMs : 0;
    }
  }
  
  // Convert to hours
  return totalMs / (1000 * 60 * 60);
};
  const getLateArrivalsRows = () => {
    return filtered.filter(row => row.isLate || row.isEarly);
  };

  const handleExportHoursPDF = () => {
    const doc = new jsPDF();
    doc.text("Hours Worked Report", 10, 10);
    autoTable(doc, {
      startY: 18,
      head: [["Name", "Total Hours"]],
      body: getHoursWorkedRows().map(row => [
        row.name,
        formatHours(row.totalHours)
      ]),
    });
    doc.save("hours_worked_report.pdf");
  };

  const handleExportHoursExcel = () => {
    const ws = XLSX.utils.json_to_sheet(getHoursWorkedRows().map(row => ({
      Name: row.name,
      "Total Hours": formatHours(row.totalHours)
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HoursWorked");
    XLSX.writeFile(wb, "hours_worked_report.xlsx");
  };

  const handleExportLatePDF = () => {
    const doc = new jsPDF();
    doc.text("Late Arrivals Report", 10, 10);
    autoTable(doc, {
      startY: 18,
      head: [["Date", "Name", "Late Check-In", "Early Check-Out"]],
      body: getLateArrivalsRows().map(row => [
        row.date,
        row.name,
        row.isLate ? new Date(row.checkIn).toLocaleTimeString() : "-",
        row.isEarly ? new Date(row.checkOut).toLocaleTimeString() : "-"
      ]),
    });
    doc.save("late_arrivals_report.pdf");
  };

  const handleExportLateExcel = () => {
    const ws = XLSX.utils.json_to_sheet(getLateArrivalsRows().map(row => ({
      Date: row.date,
      Name: row.name,
      "Late Check-In": row.isLate ? new Date(row.checkIn).toLocaleTimeString() : "-",
      "Early Check-Out": row.isEarly ? new Date(row.checkOut).toLocaleTimeString() : "-"
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LateArrivals");
    XLSX.writeFile(wb, "late_arrivals_report.xlsx");
  };

  return (
    <div className="admin-root">
      <div className="admin-sidebar">
        <h2>Admin Panel</h2>
        <button
          className={`sidebar-btn${activeTab === "attendance" ? " active" : ""}`}
          onClick={() => setActiveTab("attendance")}
        >
          Attendance Report
        </button>
        <button
          className={`sidebar-btn${activeTab === "hours" ? " active" : ""}`}
          onClick={() => setActiveTab("hours")}
        >
          Hours Worked
        </button>
        <button
          className={`sidebar-btn${activeTab === "late" ? " active" : ""}`}
          onClick={() => setActiveTab("late")}
        >
          Late Arrivals
        </button>
        <button
          className="sidebar-btn logout"
          onClick={() => {
            auth.signOut();
            window.location.href = "/";
          }}
        >
          Logout
        </button>
      </div>

      <div className="admin-main">
        <div className="admin-header">
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
            <button className="range-btn" onClick={() => setShowRange(!showRange)}>
              {range[0].startDate && range[0].endDate
                ? `${range[0].startDate.toISOString().split("T")[0]} - ${range[0].endDate.toISOString().split("T")[0]}`
                : "Select Date Range"}
            </button>
            {showRange && (
              <div className="fixed-element">
                <DateRange
                  editableDateInputs={true}
                  onChange={item => setRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={range}
                  maxDate={new Date(new Date().setHours(23,59,59,999))}
                  locale={enUS}
                />
                <button className="range-btn" onClick={() => setShowRange(false)}>Close</button>
              </div>
            )}
            <button className="showall-btn" onClick={handleShowAll}>
              Clear Range
            </button>
          </div>

          <input
            type="text"
            placeholder="Search by name"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            className="search-input"
          />

          <button className="search-btn" onClick={handleSearch}>Search</button>
          <button className="showall-btn" onClick={handleShowAll}>Show All</button>

          {activeTab === "attendance" && (
            <>
              <button className="export-btn" onClick={handleExportExcel}>Export Excel</button>
              <button className="export-btn" onClick={handleExportPDF}>Export PDF</button>
            </>
          )}
          {activeTab === "hours" && (
            <>
              <button className="export-btn" onClick={handleExportHoursExcel}>Export Excel</button>
              <button className="export-btn" onClick={handleExportHoursPDF}>Export PDF</button>
            </>
          )}
          {activeTab === "late" && (
            <>
              <button className="export-btn" onClick={handleExportLateExcel}>Export Excel</button>
              <button className="export-btn" onClick={handleExportLatePDF}>Export PDF</button>
            </>
          )}
        </div>

        <div className="admin-table-container">
          {activeTab === "attendance" ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Check In</th>
                  <th>Lunch Start</th>
                  <th>Lunch End</th>
                  <th>Check Out</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 24 }}>
                      Loading...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 24 }}>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.date}</td>
                      <td>{row.name}</td>
                      <td>{row.checkIn ? new Date(row.checkIn).toLocaleTimeString() : ""}</td>
                      <td>{row.lunchStart ? new Date(row.lunchStart).toLocaleTimeString() : ""}</td>
                      <td>{row.lunchEnd ? new Date(row.lunchEnd).toLocaleTimeString() : ""}</td>
                      <td>{row.checkOut ? new Date(row.checkOut).toLocaleTimeString() : ""}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : activeTab === "hours" ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Total Hours Worked</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: 24 }}>
                      Loading...
                    </td>
                  </tr>
                ) : getHoursWorkedRows().length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: 24 }}>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  getHoursWorkedRows().map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.name}</td>
                      <td>{formatHours(row.totalHours)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Late Check-In</th>
                  <th>Early Check-Out</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                      Loading...
                    </td>
                  </tr>
                ) : getLateArrivalsRows().length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                      No late arrivals found.
                    </td>
                  </tr>
                ) : (
                  getLateArrivalsRows().map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.date}</td>
                      <td>{row.name}</td>
                      <td style={{ color: row.isLate ? "#ef4444" : "#6b7280" }}>
                        {row.isLate ? new Date(row.checkIn).toLocaleTimeString() : "-"}
                      </td>
                      <td style={{ color: row.isEarly ? "#ef4444" : "#6b7280" }}>
                        {row.isEarly ? new Date(row.checkOut).toLocaleTimeString() : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}